import { useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Trash2, Upload } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSpecSchema } from "@/context/SpecSchemaContext";
import { CountrySelect } from "@/components/CountrySelect";
import { Btn, Card, Empty, Field, Input, Select, Textarea } from "@/components/Ui";

export default function SupplierCatalogueImport() {
  const schema = useSpecSchema();
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [engine, setEngine] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  const extract = async () => {
    if (!file) return;
    setBusy(true);
    setDone(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/supplier/submissions/extract-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setRows(data.rows);
      setEngine(data.engine);
      toast.success(`${data.count} product${data.count === 1 ? "" : "s"} drafted — please review`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const setRow = (i, k, v) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
  const setSpec = (i, k, v) =>
    setRows((r) => r.map((row, j) => (j === i ? { ...row, spec: { ...row.spec, [k]: v } } : row)));
  const drop = (i) => setRows((r) => r.filter((_, j) => j !== i));

  const submit = async () => {
    const bad = rows.find((r) => !r.part_number?.trim() || !r.name?.trim() || !r.manufacturer_name?.trim());
    if (bad) return toast.error("Part number, name and manufacturer are required on every row");
    setSubmitting(true);
    try {
      const { data } = await api.post("/supplier/submissions/batch", {
        source_filename: file?.name || null,
        rows: rows.map((r) => ({
          ...r,
          list_price: r.list_price === "" || r.list_price == null ? null : Number(r.list_price),
          spec: Object.fromEntries(Object.entries(r.spec || {}).filter(([, v]) => v !== "" && v != null)),
        })),
      });
      setDone(data);
      setRows(null);
      setFile(null);
      toast.success(`${data.submitted} row(s) submitted for ADIT approval`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="supplier-catalogue-import">
      <Card>
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-[#147D78]" />
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
              Import from a PDF catalogue
            </h2>
            <p className="mt-1 text-sm text-[#667788]">
              Upload your product catalogue and our AI drafts one row per part number. Nothing is
              published until you review the rows below and the ADIT team approves them.
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <input ref={fileRef} data-testid="catalogue-file-input" type="file" accept=".pdf"
            className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setRows(null); }} />
          <Btn variant="ghost" data-testid="catalogue-pick-btn" onClick={() => fileRef.current?.click()}
            className="border-dashed border-[#147D78] bg-[#f2f8f7] text-[#147D78]">
            <Upload className="h-4 w-4" /> {file ? file.name : "Choose PDF catalogue"}
          </Btn>
          <Btn variant="teal" data-testid="catalogue-extract-btn" loading={busy} disabled={!file}
            onClick={extract}>
            {busy ? "Reading catalogue…" : "Extract products"}
          </Btn>
        </div>
        <p className="mt-2 text-xs text-[#8598ac]">
          Text-based PDF, max 20 MB. Scanned image-only catalogues cannot be read — use the Excel
          template on My Products instead.
        </p>
        {busy && (
          <p className="mt-3 text-xs font-semibold text-[#147D78]" data-testid="catalogue-busy">
            This can take up to a couple of minutes for a large catalogue.
          </p>
        )}
      </Card>

      {done && (
        <Card className="mt-5" data-testid="catalogue-done">
          <p className="text-sm font-semibold text-[#13795B]">
            {done.submitted} row(s) submitted for ADIT approval.
          </p>
          <p className="mt-1 text-sm text-[#667788]">
            Track their status on My Products — approved rows go live in the searchable catalogue.
          </p>
        </Card>
      )}

      {rows && rows.length === 0 && (
        <Empty testId="catalogue-empty" title="No products found in that PDF"
          sub="Try a catalogue with a product/price table, or use the Excel template." />
      )}

      {rows && rows.length > 0 && (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-[#334e68]" data-testid="catalogue-row-count">
              {rows.length} draft row{rows.length === 1 ? "" : "s"}
              {engine ? ` · read by ${engine}` : ""} · AI draft, please verify every value
            </p>
            <Btn variant="gold" data-testid="catalogue-submit-btn" loading={submitting} onClick={submit}>
              Submit {rows.length} row{rows.length === 1 ? "" : "s"} for approval
            </Btn>
          </div>
          <div className="mt-4 space-y-4">
            {rows.map((r, i) => (
              <Card key={i} data-testid={`catalogue-row-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="font-display text-base font-bold text-[#102A43]">
                    {r.part_number || "Untitled row"}
                  </p>
                  <button type="button" data-testid={`catalogue-drop-${i}`} onClick={() => drop(i)}
                    className="text-[#B42318]" aria-label="Remove row">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                  <Field label="Part number *">
                    <Input data-testid={`catalogue-part-${i}`} value={r.part_number || ""}
                      onChange={(e) => setRow(i, "part_number", e.target.value)} />
                  </Field>
                  <Field label="Product name *">
                    <Input data-testid={`catalogue-name-${i}`} value={r.name || ""}
                      onChange={(e) => setRow(i, "name", e.target.value)} />
                  </Field>
                  <Field label="Manufacturer *">
                    <Input data-testid={`catalogue-mfr-${i}`} value={r.manufacturer_name || ""}
                      onChange={(e) => setRow(i, "manufacturer_name", e.target.value)} />
                  </Field>
                  <Field label="Brand">
                    <Input data-testid={`catalogue-brand-${i}`} value={r.brand || ""}
                      onChange={(e) => setRow(i, "brand", e.target.value)} />
                  </Field>
                  <Field label="Category">
                    <Select data-testid={`catalogue-category-${i}`} value={r.category || ""}
                      onChange={(e) => setRow(i, "category", e.target.value)}
                      options={["", ...schema.published]} />
                  </Field>
                  <Field label="Subcategory">
                    <Input data-testid={`catalogue-subcategory-${i}`} value={r.subcategory || ""}
                      onChange={(e) => setRow(i, "subcategory", e.target.value)} />
                  </Field>
                  <Field label="List price">
                    <Input data-testid={`catalogue-price-${i}`} type="number" step="0.01"
                      value={r.list_price ?? ""} onChange={(e) => setRow(i, "list_price", e.target.value)} />
                  </Field>
                  <Field label="Currency">
                    <Input data-testid={`catalogue-currency-${i}`} value={r.currency || "AED"}
                      onChange={(e) => setRow(i, "currency", e.target.value)} />
                  </Field>
                  <Field label="Country of manufacture">
                    <CountrySelect testId={`catalogue-country-${i}`} value={r.country_of_manufacture || ""}
                      onChange={(v) => setRow(i, "country_of_manufacture", v)} />
                  </Field>
                </div>
                {schema.inputsFor(r.category).length > 0 && (
                  <div className="mt-4 grid gap-4 border-t border-[#DFE6ED] pt-4 sm:grid-cols-3">
                    {schema.inputsFor(r.category).map(([k, l, type, options]) => (
                      <Field key={k} label={l}>
                        {options && options.length ? (
                          <Select data-testid={`catalogue-spec-${i}-${k}`} value={r.spec?.[k] ?? ""}
                            options={[["", "—"], ...options.map((o) => [o, o])]}
                            onChange={(e) => setSpec(i, k, e.target.value)} />
                        ) : (
                          <Input data-testid={`catalogue-spec-${i}-${k}`} type={type} step="0.1"
                            value={r.spec?.[k] ?? ""}
                            onChange={(e) => setSpec(i, k, type === "number" && e.target.value !== ""
                              ? Number(e.target.value) : e.target.value)} />
                        )}
                      </Field>
                    ))}
                  </div>
                )}
                <div className="mt-4">
                  <Field label="Technical notes for buyers">
                    <Textarea rows={2} data-testid={`catalogue-notes-${i}`} value={r.technical_notes || ""}
                      onChange={(e) => setRow(i, "technical_notes", e.target.value)} />
                  </Field>
                </div>
              </Card>
            ))}
          </div>
          <Btn variant="gold" data-testid="catalogue-submit-btn-bottom" loading={submitting}
            onClick={submit} className="mt-5 w-full sm:w-auto">
            Submit {rows.length} row{rows.length === 1 ? "" : "s"} for approval
          </Btn>
        </>
      )}
    </div>
  );
}
